import { useState, useRef, useCallback, useEffect } from "react";
import axios from "axios";
import request, { storeRotatedToken } from "./requests";
import { toast } from "./SnackBarUtils";

const DEFAULT_FOCAL_X = 50;
const DEFAULT_FOCAL_Y = 30;
const MAX_DIMENSION = 2000;
const JPEG_QUALITY = 0.85;
const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const NOT_A_PHOTO = "Please choose a photo. Videos and other files can't be used.";
const TOO_LARGE = "That photo is too large. Please use one under 10MB.";
const UPLOAD_FAILED = "Error uploading profile image";

const loadImage = (file) =>
  new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("decode failed"));
    };
    img.src = url;
  });

const shrinkPhoto = async (file) => {
  try {
    const img = await loadImage(file);
    const width = img.naturalWidth;
    const height = img.naturalHeight;
    if (!width || !height) return file;

    const scale = Math.min(1, MAX_DIMENSION / Math.max(width, height));
    const canvas = document.createElement("canvas");
    canvas.width = Math.round(width * scale);
    canvas.height = Math.round(height * scale);
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

    const blob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY)
    );
    if (!blob) return file;
    if (scale === 1 && blob.size >= file.size) return file;
    return blob;
  } catch (e) {
    return file;
  }
};

const uploadErrorMessage = (e) => {
  const status = e?.response?.status;
  if (status === 413) return TOO_LARGE;
  if (status === 400 && e?.response?.data?.error) return e.response.data.error;
  return UPLOAD_FAILED;
};

const useProfilePhoto = ({ onUploaded, onFailed } = {}) => {
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const previewRef = useRef(null);
  const lastImageUrl = useRef(null);
  const keepPreview = useRef(false);
  const handlers = useRef({ onUploaded, onFailed });
  handlers.current = { onUploaded, onFailed };

  useEffect(
    () => () => {
      if (previewRef.current) URL.revokeObjectURL(previewRef.current);
    },
    []
  );

  const setPreview = useCallback((url) => {
    if (previewRef.current && previewRef.current !== url) {
      URL.revokeObjectURL(previewRef.current);
    }
    previewRef.current = url;
    setPreviewUrl(url);
  }, []);

  const syncToProfileImage = useCallback((imageUrl) => {
    if (keepPreview.current) {
      lastImageUrl.current = imageUrl;
      return;
    }
    if (previewRef.current && imageUrl && imageUrl !== lastImageUrl.current) {
      URL.revokeObjectURL(previewRef.current);
      previewRef.current = null;
      setPreviewUrl(null);
    }
    lastImageUrl.current = imageUrl;
  }, []);

  const upload = useCallback(
    async (file) => {
      if (!file) return false;

      if (file.type && !file.type.startsWith("image/")) {
        toast.error(NOT_A_PHOTO);
        return false;
      }

      keepPreview.current = false;
      setPreview(URL.createObjectURL(file));
      setLoading(true);

      const photo = await shrinkPhoto(file);
      if (photo !== file) setPreview(URL.createObjectURL(photo));
      if (photo.size > MAX_UPLOAD_BYTES) {
        setPreview(null);
        setLoading(false);
        toast.error(TOO_LARGE);
        if (handlers.current.onFailed) handlers.current.onFailed();
        return false;
      }

      const body = new FormData();
      body.append("photo", photo, photo === file ? file.name : "photo.jpg");
      const token = localStorage.getItem(
        `${process.env.REACT_APP_COGNITO_CLIENT_ID}.accessToken`
      );

      try {
        const result = await axios({
          url: `${process.env.REACT_APP_TNFT_SERVER}/profile/photo`,
          method: "post",
          data: body,
          headers: {
            "Content-Type": "multipart/form-data",
            Accesstoken: token,
          },
        });
        storeRotatedToken(result);
        await request("profile/update", "post", {
          photoFocalX: DEFAULT_FOCAL_X,
          photoFocalY: DEFAULT_FOCAL_Y,
        });
        keepPreview.current = true;
        toast.success("Profile image updated");
        setLoading(false);
        if (handlers.current.onUploaded) handlers.current.onUploaded();
        return true;
      } catch (e) {
        storeRotatedToken(e?.response);
        setPreview(null);
        setLoading(false);
        toast.error(uploadErrorMessage(e));
        if (handlers.current.onFailed) handlers.current.onFailed();
        return false;
      }
    },
    [setPreview]
  );

  return {
    loading,
    previewUrl,
    syncToProfileImage,
    upload,
    defaultFocal: { x: DEFAULT_FOCAL_X, y: DEFAULT_FOCAL_Y },
  };
};

export default useProfilePhoto;
